/**
 * Goosecross variant — UI overrides + extras for tenant `goosecross`.
 *
 * REGISTERED EXTRAS:
 *   - kind: 'contact-options' — base ContactOptions bubble (Text/Email/Call menu)
 *   - kind: 'live-person-form' — base LivePersonForm (RedChirp SMS opt-in)
 *   - kind: 'reviews-buttons' — Goose-specific buttons row (Google/Yelp/TripAdvisor)
 *   - Component override: MembershipCard, EventCard, EventDetailsCard, PromoPoster
 *
 * WIRED EVENTS (mirror old script.js contact flow + reviews short-circuit):
 *   - ws-prompt-select '__contact__'  → ContactOptions menu
 *   - ws-prompt-select '__reviews__'  → ReviewsButtons row (no LLM call)
 *   - ws-contact-text  → LivePersonForm intro + form
 *   - ws-contact-submit → confirmation (client-side; see RedChirp note below)
 *   - ws-contact-call  → tel: link follow-up + re-prompt
 *   - ws-contact-email → email follow-up + re-prompt
 *
 * REDCHIRP STATUS:
 *   The BE endpoint POST /api/v1/integrations/redchirp/contact-request/ exists
 *   (apps/integrations/redchirp/views.py) but requires admin/operator auth. The
 *   anonymous widget cannot call it directly — confirmation is shown client-side
 *   for now, matching the old behaviour. When BE ships a public route, swap the
 *   ws-contact-submit body for a fetch() to that endpoint. Form fields already
 *   align with ContactRequestSerializer (first_name, last_name, mobile,
 *   help_type, message).
 *
 * Reference: goosecross/script.js Lines 1428–1444 (welcome prompts),
 *   2922–2949 (contact menu), 2996–3074 (text-a-live-person form),
 *   2096–2115 (reviews buttons).
 */

import { on, addMessage, getState, setState } from "../../core/state.js";
import { getContact } from "../../core/configAccessors.js";
import { registerMessageKind } from "../../components/base/messages/index.js";
import { registry } from "../../core/registry.js";
import ContactOptions from "../../components/base/messages/ContactOptions.js";
import LivePersonForm from "../../components/base/messages/LivePersonForm.js";
import GooseWineCard from "./components/WineCard.js";
import GooseEventCard from "./components/EventCard.js";
import GooseEventDetailsCard from "./components/EventDetailsCard.js";
import GoosePromoPoster from "./components/PromoPoster.js";
import GooseMembershipCard from "./components/MembershipCard.js";
import GoosePromptList from "./components/PromptList.js";
import GooseHeader from "./components/Header.js";

// Keep the configured price on its own line, as in the original Goose banner.
function formatBannerText(text) {
  if (typeof text !== "string") return text;
  return text.replace(/\s+(\$\s*\d[\s\S]*)$/, "\n$1");
}

// Guided chips are admin-configured (tenant.guided_flow_questions) — no list is
// hard-coded here. The only per-session rule that can't live in static config:
// the tasting chip is hidden for QR entries unless openagent forces it (old
// script.js 1431). Apply it over the configured list.
function applyQrFilter(chips) {
  const { isQR, openagent } = getState().session || {};
  const showTasting = !isQR || openagent;
  return chips.filter(
    (item) => showTasting || item.value !== "Show me all tasting experiences"
  );
}

// After a contact follow-up, re-prompt with the guided buttons. Mirrors old
// showDefaultOptions (script.js 2522).
function postDefaultOptions() {
  addMessage({ role: "bot", kind: "text", data: { text: "Is there anything else I can help you with?" } });
  const items = (getState().tenant || {}).guided_flow_questions || [];
  addMessage({ role: "bot", kind: "prompts", data: { items } });
}

// tel: href from a display phone (strip spaces/parens, keep +digits).
function telHref(phone) {
  return `tel:${String(phone || "").replace(/[^+0-9]/g, "")}`;
}

// Host-page search bar. Old main.js hard-coded all of this (2793 suggestions,
// 2857 createSearchBar, 2929 placeholder); here it is only a FALLBACK — the
// moment `search_bar` is set in admin tenant config, that wins (variants/index.js).
// The selectors are Goosecross's own Webflow nav: the bar goes into the top nav
// row, immediately before the burger, exactly where the live site puts it.
export function getSearchBar() {
  return {
    mount: {
      selector: ".w-layout-hflex.top-nav",
      position: "before",
      reference: "#menu-button",
    },
    placeholder: "Search for or ask anything about Goosecross \u{1F347}",
    loading_text: "Generating response for you...",
    min_width: 1024,
    suggestions: [
      { category: "Suggestions", text: "Join the Goosecross Wine Club" },
      { category: "Suggestions", text: "2024 Goosecross Napa Valley Sauvignon Blanc" },
      { category: "Suggestions", text: "Host a private wine party in your home with Goosecross" },
      { category: "Wine Tasting", text: "Where can i book a Matinee Signature Wine Tasting at Goosecross?" },
      { category: "Wine Tasting", text: "How much is the Signature Wine Tasting at Goosecross Winery?" },
      { category: "Wine Tasting", text: "What is included in the State Lane Estate Tasting experience?" },
    ],
  };
}

export async function register({ config, shadow }) {
  // Custom message kinds.
  registerMessageKind("contact-options", ContactOptions);
  registerMessageKind("live-person-form", LivePersonForm);
  registerMessageKind("prompts", GoosePromptList);

  // Reviews are BE-driven (reviews_information → "review" card kind), rendered
  // per-item by this override. Parity with old createReviews (renders the BE's
  // review dict), NOT a hardcoded sentinel.

  // Component overrides — Goose-specific cards.
  registry.registerComponent("WineCard", GooseWineCard);
  registry.registerComponent("Header", GooseHeader);
  registry.registerComponent("EventCard", GooseEventCard);
  registry.registerComponent("EventDetailsCard", GooseEventDetailsCard);
  registry.registerComponent("PromoPoster", GoosePromoPoster);
  registry.registerComponent("MembershipCard", GooseMembershipCard);

  // Welcome guided buttons come from tenant config (guided_flow_questions),
  // edited in admin. Apply the per-session QR filter over the configured list;
  // MessageList._seed renders tenant.guided_flow_questions.
  // Keep promo strip undimmed so the white BOOK NOW pill (old .promo-text-right)
  // reads clearly on the bronze banner — matches ss1.
  // BOOK NOW → ExploreTock (old shopNowBanner URL on previousBanner).

  const cfg = getState().tenant || {};
  const chips = Array.isArray(cfg.guided_flow_questions) ? cfg.guided_flow_questions : [];
  const theme = {
    ...(cfg.theme || {}),
    banner_text: formatBannerText(cfg.theme?.banner_text),
    banner_text_alt: formatBannerText(cfg.theme?.banner_text_alt),
    banner_promo_dim: false,
    // The welcome hero is display-only; booking uses the separate CTA.
    banner_link_url: null,
    banner_link_url_alt: null,
    ...((cfg.theme || {}).banner && {
      banner: {
        ...cfg.theme.banner,
        text: formatBannerText(cfg.theme.banner.text),
        text_alt: formatBannerText(cfg.theme.banner.text_alt),
        link_url: null,
        link_url_alt: null,
      },
    }),
    banner_cta_label: (cfg.theme || {}).banner_cta_label || "BOOK NOW",

    // Prefer URL open over chat query (old shopNowBanner opened a tab).
    banner_cta_query: null,
    // Old defaultCardLogo — keep whichever admin key is set.
    card_logo_url:
      (cfg.theme || {}).card_logo_url ||
      (cfg.theme || {}).membership_card_image ||
      null,
  };
  setState({
    tenant: {
      ...cfg,
      theme,
      guided_flow_questions: applyQrFilter(chips),
    },
  });

  // Contact our team → "How would you like to connect?" + ContactOptions menu
  // (old showContactOptions, script.js 2922-2949). No login prompt here.
  on("ws-prompt-select", ({ value }) => {
    if (value === "__contact__") {
      addMessage({
        role: "bot",
        kind: "text",
        data: { text: "How would you like to connect with our team?" },
      });
      addMessage({
        role: "bot",
        kind: "contact-options",
        data: { email: getContact("email"), phone: getContact("phone") },
      });
      return;
    }
    // Reviews are now BE-driven (the chip sends "Show me all the reviews"); the
    // BE returns reviews_information rendered by the GooseReviewCard override.
  });

  // Text a Live Person → RedChirp opt-in form (old showContactForm,
  // script.js 2996-3074). Intro + form bubble.
  on("ws-contact-text", () => {
    addMessage({
      role: "bot",
      kind: "text",
      data: { text: "Enter your information and we will text you between the hours of 9AM to 5PM PST." },
    });
    const brand = (getState().tenant || {}).display_name || "the winery";
    addMessage({ role: "bot", kind: "live-person-form", data: { brand } });
  });

  // Live-person form submitted. BE endpoint exists but is admin-auth gated
  // (apps/integrations/redchirp/views.py — IsAuthenticated + HasRole). Until a
  // public route ships, confirmation is client-side — mirrors Fogarty stance
  // and the old anonymous-POST behaviour seen end-to-end by the user.
  on("ws-contact-submit", () => {
    addMessage({
      role: "bot",
      kind: "text",
      data: { text: "We've received your information and we will text you between the hours of 9AM to 5PM PST." },
    });
    postDefaultOptions();
  });

  // Call Us → markdown tel link + re-prompt (old showContactNumber).
  on("ws-contact-call", () => {
    const phone = getContact("phone");
    addMessage({
      role: "bot",
      kind: "text",
      data: { text: `You can call us at [${phone}](${telHref(phone)}). Our team is available during business hours.` },
    });
    postDefaultOptions();
  });

  // Email Us → follow-up + re-prompt (old showContactEmailMessage). The mailto
  // itself is opened by the ContactOptions link.
  on("ws-contact-email", () => {
    const phone = getContact("phone");
    addMessage({
      role: "bot",
      kind: "text",
      data: { text: `We'll get back to you within 24–48 hours. For faster assistance, please call us at [${phone}](${telHref(phone)}). Our team is available during business hours.` },
    });
    postDefaultOptions();
  });
}
